import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FaFileInvoiceDollar,
  FaDownload,
  FaChartBar,
  FaTable,
} from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../../master/academicConfiguration/Academiclist.module.css";
import reportStyles from "./Report.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomDatePicker,
} from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import CustomBarChart from "../../../../components/Chart/CustomBarChart";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  fetchAcademicYearOptions,
  fetchClassOptions,
  fetchSectionOptions,
  clearSectionOptions,
} from "../../../../redux/Masterdata";
import { buildReportPdfHtml } from "./pdfReportTemplate";

const EMPTY_SUMMARY = {
  students_benefited: 0,
  total_discounted_amount: 0,
  avg_discount_per_student: 0,
};

/* Both endpoints return the same `avrg_data` block. */
const readSummary = (avrgData) => ({
  students_benefited: Number(avrgData?.students_benefited) || 0,
  total_discounted_amount: Number(avrgData?.total_discounted_amount) || 0,
  avg_discount_per_student: Number(avrgData?.avg_discount_per_student) || 0,
});

/* ── ₹1,000 — amounts arrive as strings ("1000.00") ── */
const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);

/* ── "2026-05-05" -> "05/05/2026" ── */
const formatDate = (val) => {
  if (!val) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(val);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return val;
};

const toApiDate = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "");

/* CustomDropdown hands back the whole option (or null when cleared). */
const optionValue = (v) => (typeof v === "object" && v !== null ? v.value : v);

/* Round the chart's y-axis up to a readable ceiling and pick a 5-step grid,
   so ₹78,000 renders against a ₹1L axis rather than a ragged max. */
const buildAxis = (values) => {
  const peak = Math.max(0, ...values.map((v) => Number(v) || 0));
  if (!peak) return { maxY: 100, stepSize: 20 };
  const magnitude = Math.pow(10, Math.floor(Math.log10(peak)));
  const maxY = Math.ceil(peak / magnitude) * magnitude;
  return { maxY, stepSize: maxY / 5 };
};

const DiscountReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { translations } = useSelector((state) => state.language);
  // memoised so the `|| {}` fallback doesn't create a new object each render
  // and re-trigger every useMemo/useCallback that depends on it
  const labels = useMemo(() => translations?.labels || {}, [translations]);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const master_id = clientData?.master_id || "";

  const {
    academicYearOptions: reduxAcademicYearOptions,
    academicYearLoading,
    classOptions: reduxClassOptions,
    classLoading,
    sectionOptions: reduxSectionOptions,
    sectionLoading,
  } = useSelector((state) => state.masterData);

  // ── Filters ──
  const [academicYear, setAcademicYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [discountCategory, setDiscountCategory] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  // ── View switch — chart is the default, table icon flips it ──
  const [view, setView] = useState("chart");

  // ── Chart feed ──
  const [chartData, setChartData] = useState([]);
  const [chartSummary, setChartSummary] = useState(EMPTY_SUMMARY);
  const [chartLoading, setChartLoading] = useState(false);

  // ── Grid feed — its avrg_data narrows with search, so it is kept apart
  //    from the chart's and only the active view's block feeds the cards ──
  const [data, setData] = useState([]);
  const [tableSummary, setTableSummary] = useState(EMPTY_SUMMARY);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [exporting, setExporting] = useState("");

  const summary = view === "chart" ? chartSummary : tableSummary;

  // ── Academic years on mount ──
  useEffect(() => {
    if (school_id) dispatch(fetchAcademicYearOptions(school_id));
  }, [dispatch, school_id]);

  // ── Default-select the active year (isActive === 1) once options land ──
  useEffect(() => {
    if (academicYear || reduxAcademicYearOptions.length === 0) return;
    const active =
      reduxAcademicYearOptions.find((o) => o.isActive === 1) ||
      reduxAcademicYearOptions[0];
    if (active) setAcademicYear(active.value);
  }, [reduxAcademicYearOptions, academicYear]);

  // ── The `id` behind the selected academic_year string — the report and
  //    discount-list payloads key off the id, the class list off the label ──
  const academicYearId = useMemo(
    () =>
      reduxAcademicYearOptions.find((o) => o.value === academicYear)?.id || "",
    [reduxAcademicYearOptions, academicYear],
  );

  // ── Classes depend on the selected academic year ──
  useEffect(() => {
    if (!school_id || !academicYear) return;
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academicYear,
      }),
    );
  }, [dispatch, school_id, academicYear]);

  // ── Discount categories — schoolFees/discountList, scoped to the selected
  //    academic year. Local, not a Masterdata feed. ──
  useEffect(() => {
    if (!school_id || !academicYearId) return;
    let cancelled = false;

    const fetchCategories = async () => {
      setCategoryLoading(true);
      try {
        const response = await AxiosInstance.post("schoolFees/discountList", {
          school_id: Number(school_id),
          academic_year_id: Number(academicYearId),
          dis_type: "",
          search: "",
          page: 1,
          limit: 500,
        });
        const ok = response?.data?.status || response?.data?.success;
        if (!cancelled) {
          setCategoryList(
            ok && Array.isArray(response.data.data)
              ? response.data.data.map((item) => ({
                  value: String(item.id),
                  label: item.name || "",
                }))
              : [],
          );
        }
      } catch (error) {
        console.error("Error fetching discount categories:", error);
        if (!cancelled) setCategoryList([]);
      } finally {
        if (!cancelled) setCategoryLoading(false);
      }
    };

    fetchCategories();
    return () => {
      cancelled = true;
    };
  }, [school_id, academicYearId]);

  // ── Dropdown options — Class/Section/Category carry an "All" entry ──
  const academicYearOptions = useMemo(
    () =>
      reduxAcademicYearOptions.map((o) => ({ value: o.value, label: o.label })),
    [reduxAcademicYearOptions],
  );

  const classOptions = useMemo(
    () => [{ value: "", label: labels.all || "All" }, ...reduxClassOptions],
    [reduxClassOptions, labels],
  );

  const sectionOptions = useMemo(
    () => [{ value: "", label: labels.all || "All" }, ...reduxSectionOptions],
    [reduxSectionOptions, labels],
  );

  const discountCategoryOptions = useMemo(
    () => [{ value: "", label: labels.all || "All" }, ...categoryList],
    [categoryList, labels],
  );

  // ── Filter block shared by the chart feed, the grid feed and the exports ──
  const filterPayload = useMemo(
    () => ({
      school_id: Number(school_id),
      academic_id: academicYearId ? Number(academicYearId) : "",
      class_id: selectedClass ? Number(selectedClass) : "",
      section_id: selectedSection || "",
      discount_id: discountCategory ? Number(discountCategory) : "",
      from_date: toApiDate(fromDate),
      to_date: toApiDate(toDate),
    }),
    [
      school_id,
      academicYearId,
      selectedClass,
      selectedSection,
      discountCategory,
      fromDate,
      toDate,
    ],
  );

  // ── Grid payload — filters plus search / page. Sorting stays client-side
  //    (the table's own behaviour), so the sort keys go out empty. ──
  const buildTablePayload = useCallback(
    (page, limit) => ({
      ...filterPayload,
      search: apiSearch.trim(),
      sort_by: "",
      sort_dir: "",
      page,
      limit,
    }),
    [filterPayload, apiSearch],
  );

  const transformRows = useCallback(
    (rows, offset = 0) =>
      rows.map((item, index) => ({
        s_no: offset + index + 1,
        chalan_no: item.chalan_no || "",
        emis_id: item.emis_id || item.admission_id || "",
        student_name: item.student_name || "",
        class_name: item.class || item.class_name || "",
        section_id: item.section_name || item.section_id || "",
        discount_category: item.discount_category || "",
        fee_type: item.fee_type_name || "",
        discounted_amount: Number(item.discounted_amount) || 0,
        date: item.date || "",
        time: item.time || "",
      })),
    [],
  );

  const filtersReady = Boolean(school_id && academicYearId);

  // ── Chart feed — only while the chart is on screen ──
  useEffect(() => {
    if (view !== "chart") return undefined;
    if (!filtersReady) {
      setChartData([]);
      setChartSummary(EMPTY_SUMMARY);
      return undefined;
    }

    let cancelled = false;
    const fetchChart = async () => {
      setChartLoading(true);
      try {
        const response = await AxiosInstance.post(
          "schoolFees/reports/discounts/chart",
          filterPayload,
        );
        if (cancelled) return;

        const ok = response?.data?.status || response?.data?.success;
        if (ok && Array.isArray(response.data.data)) {
          // CustomBarChart's non-stacked shape: one bar per discount category
          setChartData(
            response.data.data.map((c) => ({
              exam_name: c.discount_category || "",
              overAllMark: Number(c.amount_spend) || 0,
            })),
          );
          setChartSummary(readSummary(response.data.avrg_data));
        } else {
          setChartData([]);
          setChartSummary(EMPTY_SUMMARY);
        }
      } catch (error) {
        console.error("Error fetching discount chart:", error);
        if (!cancelled) {
          setChartData([]);
          setChartSummary(EMPTY_SUMMARY);
        }
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    };

    fetchChart();
    return () => {
      cancelled = true;
    };
  }, [view, filtersReady, filterPayload]);

  // ── Grid feed — only while the table is on screen ──
  useEffect(() => {
    if (view !== "table") return undefined;
    if (!filtersReady) {
      setData([]);
      setRowCount(0);
      setTableSummary(EMPTY_SUMMARY);
      return undefined;
    }

    let cancelled = false;
    const fetchTable = async () => {
      setTableLoading(true);
      try {
        const response = await AxiosInstance.post(
          "schoolFees/reports/discounts/table",
          buildTablePayload(pagination.pageIndex + 1, pagination.pageSize),
        );
        if (cancelled) return;

        const ok = response?.data?.status || response?.data?.success;
        if (ok && Array.isArray(response.data.data)) {
          setData(
            transformRows(
              response.data.data,
              pagination.pageIndex * pagination.pageSize,
            ),
          );
          setRowCount(
            response.data.pagination?.total ?? response.data.data.length,
          );
          setTableSummary(readSummary(response.data.avrg_data));
        } else {
          setData([]);
          setRowCount(0);
          setTableSummary(EMPTY_SUMMARY);
        }
      } catch (error) {
        console.error("Error fetching discount report:", error);
        if (!cancelled) {
          setData([]);
          setRowCount(0);
          setTableSummary(EMPTY_SUMMARY);
        }
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    };

    fetchTable();
    return () => {
      cancelled = true;
    };
  }, [
    view,
    filtersReady,
    buildTablePayload,
    transformRows,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  // ── Handlers ──
  const handleAcademicYearChange = useCallback(
    (value) => {
      const year = optionValue(value);
      if (!year) return; // required filter — never allowed to go empty
      setAcademicYear(year);
      setSelectedClass("");
      setSelectedSection("");
      // discount ids are scoped to a year — a stale id would filter wrongly
      setDiscountCategory("");
      dispatch(clearSectionOptions());
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleClassChange = useCallback(
    (value) => {
      const classId = optionValue(value) || "";
      setSelectedClass(classId);
      setSelectedSection("");
      dispatch(clearSectionOptions());
      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
            staff_id: master_id ? parseInt(master_id) : null,
            subject_staff: 1,
          }),
        );
      }
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [dispatch, school_id, master_id],
  );

  const handleSectionChange = useCallback((value) => {
    setSelectedSection(optionValue(value) || "");
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleCategoryChange = useCallback((value) => {
    setDiscountCategory(optionValue(value) || "");
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleFromDateChange = useCallback((d) => {
    setFromDate(d || null);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleToDateChange = useCallback((d) => {
    setToDate(d || null);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  // ── Exports pull the full filtered set, not just the visible page.
  //    In chart view rowCount is still 0, so the first call doubles as the
  //    total lookup and we re-ask once when it under-fetched. ──
  const fetchAllRows = useCallback(async () => {
    const request = async (limit) => {
      const response = await AxiosInstance.post(
        "schoolFees/reports/discounts/table",
        buildTablePayload(1, limit),
      );
      const ok = response?.data?.status || response?.data?.success;
      if (!ok || !Array.isArray(response.data.data)) return { rows: [] };
      return {
        rows: response.data.data,
        total: Number(response.data.pagination?.total) || 0,
      };
    };

    const first = await request(rowCount || pagination.pageSize);
    if (first.total > first.rows.length) {
      const full = await request(first.total);
      return transformRows(full.rows);
    }
    return transformRows(first.rows);
  }, [buildTablePayload, transformRows, rowCount, pagination.pageSize]);

  const exportHeaders = useMemo(
    () => [
      { key: "chalan_no", label: labels.chalanNo || "Chalan No." },
      {
        key: "emis_id",
        label: labels.emisIdAdmissionId || "Emis ID / Admission ID",
      },
      { key: "student_name", label: labels.studentName || "Student Name" },
      { key: "class_name", label: labels.class || "Class" },
      { key: "section_id", label: labels.section || "Section" },
      {
        key: "discount_category",
        label: labels.discountCategory || "Discount Category",
      },
      { key: "fee_type", label: labels.feeType || "Fee Type" },
      {
        key: "discounted_amount",
        label: labels.discountedAmount || "Discounted Amount",
      },
      { key: "date", label: labels.date || "Date" },
      { key: "time", label: labels.time || "Time" },
    ],
    [labels],
  );

  const fileBaseName = useMemo(
    () =>
      `${labels.discountsReport || "Discounts Report"}_${
        academicYear || ""
      }`.replace(/[^\w\-.]+/g, "_"),
    [labels, academicYear],
  );

  const handleDownloadExcel = useCallback(async () => {
    setExporting("excel");
    try {
      const rows = await fetchAllRows();
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csv = [
        exportHeaders.map((h) => escape(h.label)).join(","),
        ...rows.map((r) =>
          exportHeaders.map((h) => escape(r[h.key])).join(","),
        ),
      ].join("\r\n");

      // BOM keeps ₹ and Tamil labels readable when Excel opens the file
      const blob = new Blob([`﻿${csv}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileBaseName}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting discount report to Excel:", error);
    } finally {
      setExporting("");
    }
  }, [fetchAllRows, exportHeaders, fileBaseName]);

  const handleDownloadPdf = useCallback(async () => {
    setExporting("pdf");
    try {
      const rows = await fetchAllRows();
      const { default: html2pdf } = await import("html2pdf.js");

      const container = document.createElement("div");
      container.style.padding = "16px";
      container.innerHTML = buildReportPdfHtml({
        title: labels.discountsReport || "Discounts Report",
        infoItems: [
          clientData.school_name,
          `${labels.academicYear || "Academic Year"} ${academicYear}`,
          `${labels.reportDate || "Report Date"}: ${dayjs().format("DD/MM/YYYY")}`,
        ],
        headers: exportHeaders,
        rows,
      });

      await html2pdf()
        .set({
          margin: 8,
          filename: `${fileBaseName}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        })
        .from(container)
        .save();
    } catch (error) {
      console.error("Error exporting discount report to PDF:", error);
    } finally {
      setExporting("");
    }
  }, [
    fetchAllRows,
    exportHeaders,
    fileBaseName,
    labels,
    academicYear,
    clientData.school_name,
  ]);

  // ── Chart axis sized to the data ── ,
  const chartAxis = useMemo(
    () => buildAxis(chartData.map((d) => d.overAllMark)),
    [chartData],
  );

  // ── Columns ──
  const columns = useMemo(
    () => [
      {
        accessorKey: "chalan_no",
        header: labels.chalanNo || "Chalan No.",
        size: 130,
        enableColumnFilter: false,
      },
      {
        accessorKey: "emis_id",
        header: labels.emisIdAdmissionId || "Emis ID / Admission ID",
        size: 180,
        enableColumnFilter: false,
      },
      {
        accessorKey: "student_name",
        header: labels.studentName || "Student Name",
        enableColumnFilter: false,
      },
      {
        accessorKey: "class_name",
        header: labels.class || "Class",
        size: 100,
        enableColumnFilter: false,
      },
      {
        accessorKey: "section_id",
        header: labels.section || "Section",
        size: 110,
        enableColumnFilter: false,
      },
      {
        accessorKey: "discount_category",
        header: labels.discountCategory || "Discount Category",
        enableColumnFilter: false,
      },
      {
        accessorKey: "fee_type",
        header: labels.feeType || "Fee Type",
        enableColumnFilter: false,
      },
      {
        accessorKey: "discounted_amount",
        header: labels.discountedAmount || "Discounted Amount",
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "date",
        header: labels.date || "Date",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "time",
        header: labels.time || "Time",
        size: 110,
        enableColumnFilter: false,
      },
    ],
    [labels],
  );

  return (
    <div className={styles.container}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaFileInvoiceDollar />
        </div>
        <h1 className={styles.headerTitle}>
          {labels.discountsReport || "Discounts Report"}
        </h1>
      </div>

      {/* ── Filters ── */}
      <div className={`row ${reportStyles.filterRow}`}>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.academicYear || "Academic Year"}
            name="academicYear"
            value={academicYear}
            onChange={handleAcademicYearChange}
            options={academicYearOptions}
            placeholder={labels.selectAcademicYear || "Select Academic Year"}
            isLoading={academicYearLoading}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.class || "Class"}
            name="class"
            value={selectedClass}
            onChange={handleClassChange}
            options={classOptions}
            placeholder={labels.selectClass || "Select Class"}
            isLoading={classLoading}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.section || "Section"}
            name="section"
            value={selectedSection}
            onChange={handleSectionChange}
            options={sectionOptions}
            placeholder={labels.selectSection || "Select Section"}
            isLoading={sectionLoading}
            disabled={!selectedClass}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-12 col-sm-12 px-2">
          <div className="row">
            <div className="col-6 px-1">
              <CustomDatePicker
                label={labels.dateRange || "Date Range"}
                name="fromDate"
                value={fromDate}
                onChange={handleFromDateChange}
                placeholder={labels.fromDate || "From Date"}
                format="DD/MM/YYYY"
                size="sm"
                maxDate={toDate || undefined}
              />
            </div>
            <div className="col-6 px-1">
              <CustomDatePicker
                label="&nbsp;"
                name="toDate"
                value={toDate}
                onChange={handleToDateChange}
                placeholder={labels.toDate || "To Date"}
                format="DD/MM/YYYY"
                size="sm"
                minDate={fromDate || undefined}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.discountCategory || "Discount Category"}
            name="discountCategory"
            value={discountCategory}
            onChange={handleCategoryChange}
            options={discountCategoryOptions}
            placeholder={
              labels.selectDiscountCategory || "Select Discount Category"
            }
            isLoading={categoryLoading}
            isClearable={false}
          />
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className={`row ${reportStyles.summaryRow}`}>
        <div className="col-lg-4 col-md-12 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardStudents}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span
                className={`${reportStyles.summaryValue} ${reportStyles.summaryValueStudents}`}
              >
                {summary.students_benefited}
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.studentsBenefited || "Students Benefited"}
              </span>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-12 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardAmount}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span
                className={`${reportStyles.summaryValue} ${reportStyles.summaryValueAmount}`}
              >
                {formatCurrency(summary.total_discounted_amount)}
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.totalDiscountedAmount || "Total Discounted Amount"}
              </span>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-12 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardAverage}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span>
                <span
                  className={`${reportStyles.summaryValue} ${reportStyles.summaryValueAverage}`}
                >
                  {formatCurrency(summary.avg_discount_per_student)}
                </span>
                <span className={reportStyles.summaryUnit}>
                  {labels.perStudent || "per student"}
                </span>
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.averageDiscount || "Average Discount"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Exports + view switch ── */}
      <div className={reportStyles.toolbar}>
        <CustomButton
          type="button"
          variant="contained"
          className={`${styles.downloadButton} `}
          fullWidth={false}
          onClick={handleDownloadExcel}
          disabled={exporting !== "" || tableLoading}
        >
          <FaDownload style={{ marginRight: "8px" }} />
          {exporting === "excel"
            ? labels.downloading || "Downloading..."
            : labels.downloadAsExcel || "Download as Excel"}
        </CustomButton>
        <CustomButton
          type="button"
          variant="contained"
          className={`${styles.downloadButton} `}
          fullWidth={false}
          onClick={handleDownloadPdf}
          disabled={exporting !== "" || tableLoading}
        >
          <FaDownload style={{ marginRight: "8px" }} />
          {exporting === "pdf"
            ? labels.downloading || "Downloading..."
            : labels.downloadAsPdf || "Download as PDF"}
        </CustomButton>

        <div className={reportStyles.viewToggle}>
          <button
            type="button"
            className={`${reportStyles.viewToggleBtn} ${
              view === "chart" ? reportStyles.viewToggleBtnActive : ""
            }`}
            onClick={() => setView("chart")}
            title={labels.chartView || "Chart View"}
            aria-label={labels.chartView || "Chart View"}
            aria-pressed={view === "chart"}
          >
            <FaChartBar />
          </button>
          <button
            type="button"
            className={`${reportStyles.viewToggleBtn} ${
              view === "table" ? reportStyles.viewToggleBtnActive : ""
            }`}
            onClick={() => setView("table")}
            title={labels.tableView || "Table View"}
            aria-label={labels.tableView || "Table View"}
            aria-pressed={view === "table"}
          >
            <FaTable />
          </button>
        </div>
      </div>

      {/* ── Chart (default) / Table ── */}
      {view === "chart" ? (
        <div className={reportStyles.chartCard}>
          <CustomBarChart
            icon={<FaChartBar className={reportStyles.chartCardIcon} />}
            title={labels.discountCategoryWise || "Discount Category wise"}
            data={chartData}
            loading={chartLoading}
            maxY={chartAxis.maxY}
            stepSize={chartAxis.stepSize}
            valueLabel={labels.discountedAmount || "Discounted Amount"}
            datasetLabel={labels.discountCategory || "Discount Category"}
            compactYAxis
          />
        </div>
      ) : (
        <CustomMRTTable
          columns={columns}
          data={data}
          loading={tableLoading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          onSearchEnter={handleSearchEnter}
          placeholder={
            labels.searchByStudentNameChalanId ||
            "Search by student name, Chalan ID"
          }
          enableSearch={true}
          enableFiltering={false}
          manualPagination={true}
          localization={{
            noRecordsToDisplay: labels.noRecordsFound || "No records found",
          }}
        />
      )}
    </div>
  );
};

export default DiscountReport;
